import { router } from '@inertiajs/react';
import TeacherShell, { TeacherPageHeader } from '../../../components/layout/TeacherShell';
import { Card, Button } from '../../../components/ui';

const STATUS_PILL = {
  draft: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300',
  active: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300',
  completed: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400',
};

function formatTime(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

function csrfToken() {
  if (typeof document === 'undefined') return '';
  return document.querySelector('meta[name="csrf-token"]')?.content || '';
}

export default function TeacherLiveSessionsIndex({ live_sessions = [] }) {
  const handleEnd = (session) => {
    if (
      !confirm(
        `End the live session with ${session.invitee?.full_name || 'this student'}? They will no longer be able to join.`
      )
    ) {
      return;
    }
    router.post(
      `/teacher/live_sessions/${session.id}/complete`,
      {},
      { headers: { 'X-CSRF-Token': csrfToken() } }
    );
  };

  const open = live_sessions.filter((s) => s.status === 'active' || s.status === 'draft');
  const past = live_sessions.filter((s) => s.status === 'completed');

  return (
    <TeacherShell scrollTitle="Live Sessions">
      <TeacherPageHeader
        title="Live Sessions"
        subtitle="Resume an in-progress session or start a new one."
        actions={
          <Button variant="primary" onClick={() => router.visit('/teacher/live_sessions/new')}>
            New session
          </Button>
        }
      />

      {live_sessions.length === 0 ? (
        <Card className="p-12 text-center">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
            No live sessions yet
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
            Start one to invite a student into a real-time vocabulary session.
          </p>
          <Button variant="primary" onClick={() => router.visit('/teacher/live_sessions/new')}>
            Start a session
          </Button>
        </Card>
      ) : (
        <div className="space-y-8">
          <Section title="In progress" sessions={open} onEnd={handleEnd} emptyHint="No sessions in progress." />
          <Section title="Past sessions" sessions={past} onEnd={handleEnd} emptyHint="No past sessions." pastMode />
        </div>
      )}
    </TeacherShell>
  );
}

function Section({ title, sessions, onEnd, emptyHint, pastMode }) {
  return (
    <div>
      <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-3">
        {title}
      </h2>
      {sessions.length === 0 ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">{emptyHint}</p>
      ) : (
        <div className="space-y-2">
          {sessions.map((s) => (
            <SessionRow key={s.id} session={s} onEnd={onEnd} pastMode={pastMode} />
          ))}
        </div>
      )}
    </div>
  );
}

function SessionRow({ session, onEnd, pastMode }) {
  const isOpen = session.status === 'active' || session.status === 'draft';
  const timeLabel = isOpen
    ? `Started ${formatTime(session.started_at || session.created_at)}`
    : `Ended ${formatTime(session.completed_at)}`;

  return (
    <Card className="p-4 flex items-center gap-4">
      <div className="w-9 h-9 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center text-[11px] font-semibold text-gray-700 dark:text-gray-200">
        {session.invitee?.initials || '—'}
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-medium text-gray-900 dark:text-white truncate">
            {session.invitee?.full_name || 'No invited student'}
          </span>
          <span
            className={`px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide ${
              STATUS_PILL[session.status] || STATUS_PILL.completed
            }`}
          >
            {session.status}
          </span>
          {session.invitee?.connected && (
            <span className="text-[11px] text-green-600 dark:text-green-400 flex items-center gap-1">
              <span className="w-1.5 h-1.5 rounded-full bg-green-500" /> Connected
            </span>
          )}
        </div>
        <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
          {timeLabel}
          {session.join_code && (
            <>
              {' · '}
              <span className="font-mono tracking-widest">{session.join_code}</span>
            </>
          )}
        </div>
      </div>

      <div className="flex items-center gap-2">
        {isOpen && (
          <Button variant="primary" size="sm" onClick={() => router.visit(session.path)}>
            Resume
          </Button>
        )}
        {!isOpen && (
          <Button variant="secondary" size="sm" onClick={() => router.visit(session.path)}>
            View
          </Button>
        )}
        {isOpen && (
          <Button variant="danger" size="sm" onClick={() => onEnd(session)}>
            End
          </Button>
        )}
      </div>
    </Card>
  );
}
